import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ScreenShotShow from './ScreenShotShow.png';
import ScreenShotRate from './ScreenShotRate.png';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, setDoc, updateDoc, writeBatch, query, where } from 'firebase/firestore';
import SpielLogo from './SpielLogoWhite.svg';
import ScreenShotMain from './ScreenShotMain.png';
import ScreenShotEpisodes from './ScreenShotEpisodes.png';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [translationComplete, setTranslationComplete] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Data migration function
  const translateDataStructure = async () => {
    if (isTranslating) return;
    
    try {
      setIsTranslating(true);
      setTranslationProgress(0);
      
      // Get all users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const totalUsers = usersSnapshot.docs.length;
      let processedUsers = 0;
      
      // List of user IDs to skip show migration for
      const skipShowMigrationForUsers = [
        'FYTjHPJg2kVW0MZw2Uno0d3siTa2',
        'AAMj5y5F6EVSDxCC6Ajpu8fMTLz2',
        '3NJrm2hAP9XExsFPef4xMk1g9RO2'
      ];
      
      // Process each user
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const userId = userDoc.id;
        
        // Skip documents marked with DoNotTouch
        if (userData.DoNotTouch === true) {
          processedUsers++;
          setTranslationProgress(Math.floor((processedUsers / totalUsers) * 100));
          continue;
        }
        
        // Create batch for efficient writes
        const batch = writeBatch(db);
        
        // 1. Update user document with new structure - only keep required fields
        const newUserData = {
          id: userId,
          handle: userData.handle || '',
          email: userData.email || '',
          name: userData.name || '',
          createdAt: userData.createdAt || new Date(),
          hasCreatedPilot: false // Will update based on episodes check
        };
        
        // Check if user has episodes
        const episodesRef = collection(db, `users/${userId}/episodes`);
        const episodesSnapshot = await getDocs(episodesRef);
        newUserData.hasCreatedPilot = episodesSnapshot.docs.length > 0;
        
        // Clear existing shows in subcollection unless user is in the skip list
        if (!skipShowMigrationForUsers.includes(userId)) {
          // Get existing shows in user's subcollection
          const existingShowsRef = collection(db, `users/${userId}/shows`);
          const existingShowsSnapshot = await getDocs(existingShowsRef);
          
          // Delete existing shows
          for (const showDoc of existingShowsSnapshot.docs) {
            batch.delete(doc(db, `users/${userId}/shows`, showDoc.id));
          }
        }
        
        // 2. Create show document for user's personal show
        const showId = `show_${userId}_personal`;
        const showData = {
          id: showId,
          showId: showId,
          showName: userData.showName || `${userData.name}'s Show`,
          showImage: userData.showPicture || '',
          showDescription: userData.showBio || '',
          showCreationDate: userData.createdAt || new Date(),
          showCreator: userId,
          showCollaborators: {},
          showCastSeason: {},
          currentSeason: userData.currentSeason || 1,
          showPossibleScore: 0, // Will calculate later
          showEarnedScore: 0, // Will calculate later
          showStreak: userData.streak || 0,
          showStreakExpirationDate: null,
          streakExpirationDate: null,
          seasonScores: {} // Will populate with season scores
        };
        
        // Add collaborators if they exist
        if (userData.createdBy && Array.isArray(userData.createdBy)) {
          userData.createdBy.forEach(collaboratorId => {
            if (collaboratorId !== userId) {
              showData.showCollaborators[collaboratorId] = {
                id: collaboratorId,
                name: '', // Will need to fetch this separately
                handle: '',
                role: 'collaborator'
              };
            }
          });
        }
        
        // Add owner as collaborator
        showData.showCollaborators[userId] = {
          id: userId,
          name: userData.name || '',
          handle: userData.handle || '',
          role: 'owner'
        };
        
        // Skip show creation for specific users
        if (!skipShowMigrationForUsers.includes(userId)) {
          // 3. Create user's show reference in their subcollection
          batch.set(doc(db, `users/${userId}/shows`, showId), {
            id: showId,
            showName: showData.showName,
            addedAt: new Date()
          });
          
          // 4. Migrate cast data to show document
          // First, get all season cast data
          const seasonCastMap = {};
          for (let i = 1; i <= (userData.currentSeason || 1); i++) {
            const seasonCastRef = doc(db, `users/${userId}/seasonCast`, String(i));
            const seasonCastDoc = await getDoc(seasonCastRef);
            
            if (seasonCastDoc.exists()) {
              const castData = seasonCastDoc.data();
              seasonCastMap[String(i)] = castData.cast || [];
            } else {
              seasonCastMap[String(i)] = [];
            }
          }
          
          // Update show with cast data
          showData.showCastSeason = seasonCastMap;
          
          // 5. Migrate episodes to show's episodes subcollection and calculate season scores
          if (episodesSnapshot.docs.length > 0) {
            let showPossibleScore = 0;
            let showEarnedScore = 0;
            
            // Initialize season scores
            const seasonScores = {};
            for (let i = 1; i <= (userData.currentSeason || 1); i++) {
              seasonScores[String(i)] = {
                seasonEarnedScore: 0,
                seasonPossibleScore: 0
              };
            }
            
            for (const episodeDoc of episodesSnapshot.docs) {
              const episodeData = episodeDoc.data();
              const episodeId = episodeDoc.id;
              const episodeSeason = episodeData.season || "1";
              
              // Calculate ratings data with the new format
              const ratings = {};
              let totalScore = 0;
              let maximumPossibleScore = 0;
              
              if (episodeData.ratings) {
                Object.entries(episodeData.ratings).forEach(([raterId, ratingData]) => {
                  // Get rater's name if available
                  let raterName = '';
                  if (ratingData.raterName) {
                    raterName = ratingData.raterName;
                  } else if (ratingData.name) {
                    raterName = ratingData.name;
                  }
                  
                  // New rating format
                  ratings[raterId] = {
                    rating: ratingData.score || 0,
                    name: raterName,
                    timestamp: ratingData.timestamp || new Date(),
                    comment: ratingData.comment || ''
                  };
                  
                  totalScore += ratingData.score || 0;
                  maximumPossibleScore += 10; // Assuming 10 is max score
                });
              }
              
              // Update season scores
              if (!seasonScores[episodeSeason]) {
                seasonScores[episodeSeason] = {
                  seasonEarnedScore: 0,
                  seasonPossibleScore: 0
                };
              }
              
              seasonScores[episodeSeason].seasonEarnedScore += totalScore;
              seasonScores[episodeSeason].seasonPossibleScore += maximumPossibleScore;
              
              showPossibleScore += maximumPossibleScore;
              showEarnedScore += totalScore;
              
              // Create new episode document with correct format
              const newEpisodeData = {
                id: episodeId,
                episodeTitle: episodeData.title || '',
                episodeDescription: episodeData.description || '',
                episodeImage: episodeData.imageUrl || '',
                episodeDuration: parseInt(episodeData.duration) || 22,
                episodeCreationDate: episodeData.createdAt || new Date(),
                episodeDate: episodeData.date || new Date(),
                episodeShowId: showId,
                episodeSeason: parseInt(episodeData.season) || 1,
                episodeCreator: episodeData.createdBy || userId,
                episodeScenes: episodeData.supplementalImages || [],
                episodeRatings: ratings,
                collaborators: {}, // Will populate if needed
                episodeTotalScore: totalScore,
                episodeMaximumPossibleScore: maximumPossibleScore
              };
              
              // Add collaborators if they exist
              if (episodeData.collaborators) {
                Object.entries(episodeData.collaborators).forEach(([collabId, collabData]) => {
                  newEpisodeData.collaborators[collabId] = {
                    id: collabId,
                    name: collabData.name || '',
                    handle: collabData.handle || '',
                    role: 'co-host'
                  };
                });
              }
              
              // Set episode in show's subcollection
              batch.set(doc(db, `shows/${showId}/episodes`, episodeId), newEpisodeData);
            }
            
            // Update show with calculated scores
            showData.showPossibleScore = showPossibleScore;
            showData.showEarnedScore = showEarnedScore;
            showData.seasonScores = seasonScores;
          }
          
          // Set show document with all data
          batch.set(doc(db, 'shows', showId), showData);
        }
        
        // 6. Migrate friends to new structure
        const friendsRef = collection(db, `users/${userId}/friends`);
        const friendsSnapshot = await getDocs(friendsRef);
        
        for (const friendDoc of friendsSnapshot.docs) {
          const friendData = friendDoc.data();
          const friendId = friendDoc.id;
          
          // Create new friend document
          batch.set(doc(db, `users/${userId}/friends`, friendId), {
            id: friendId,
            friendHandle: friendData.handle || '',
            friendName: friendData.name || '',
            dateFriended: friendData.dateFriended || new Date(),
            friendUserId: friendId
          });
        }
        
        // 7. Clean up user document by setting only the new fields
        // This effectively removes all other fields
        batch.set(doc(db, 'users', userId), newUserData, { merge: false });
        
        // Commit all changes for this user
        await batch.commit();
        
        processedUsers++;
        setTranslationProgress(Math.floor((processedUsers / totalUsers) * 100));
      }
      
      setTranslationComplete(true);
    } catch (error) {
      console.error("Error translating data structure:", error);
      alert(`Translation error: ${error.message}`);
    } finally {
      setIsTranslating(false);
    }
  };

  // Add this new function to handle the special case users
  const migrateSpecialUsers = async () => {
    if (isTranslating) return;
    
    try {
      setIsTranslating(true);
      setTranslationProgress(0);
      
      // Special user IDs to migrate
      const specialUserIds = {
        '0QPeqLlcHpxcLRQxiWcP': 'SsK6Ng5lPgQ6T0oPNhz1MnX5yki2',
        'IMHE9aWD2ZE45BBA8GBN': 'wDrEIT5vDgUgUETTxrJweJa6m112'
      };
      
      let processedCount = 0;
      const totalToProcess = Object.keys(specialUserIds).length;
      
      // Process each special user
      for (const [showUserId, ownerUserId] of Object.entries(specialUserIds)) {
        // Get the show user data
        const showUserDoc = await getDoc(doc(db, 'users', showUserId));
        if (!showUserDoc.exists()) {
          console.error(`User ${showUserId} not found`);
          continue;
        }
        
        const showUserData = showUserDoc.data();
        const batch = writeBatch(db);
        
        // Create show document
        const showId = `show_${showUserId}`;
        const showData = {
          id: showId,
          showId: showId,
          showName: showUserData.showName || '',
          showImage: showUserData.showPicture || '',
          showDescription: showUserData.showBio || '',
          showCreationDate: showUserData.createdAt || new Date(),
          showCreator: ownerUserId,
          showCollaborators: {},
          showCastSeason: {},
          currentSeason: showUserData.currentSeason || 1,
          showPossibleScore: 0,
          showEarnedScore: 0,
          showStreak: 0,
          showStreakExpirationDate: null,
          streakExpirationDate: null,
          seasonScores: {}
        };
        
        // Add owner as collaborator
        showData.showCollaborators[ownerUserId] = {
          id: ownerUserId,
          name: showUserData.name || '',
          handle: showUserData.handle || '',
          role: 'owner'
        };
        
        // Add cast data
        if (showUserData.season1Cast && Array.isArray(showUserData.season1Cast)) {
          showData.showCastSeason['1'] = showUserData.season1Cast;
        }
        
        // Get episodes
        const episodesRef = collection(db, `users/${showUserId}/episodes`);
        const episodesSnapshot = await getDocs(episodesRef);
        
        if (episodesSnapshot.docs.length > 0) {
          let showPossibleScore = 0;
          let showEarnedScore = 0;
          
          // Initialize season scores
          const seasonScores = {};
          for (let i = 1; i <= (showUserData.currentSeason || 1); i++) {
            seasonScores[String(i)] = {
              seasonEarnedScore: 0,
              seasonPossibleScore: 0
            };
          }
          
          // Process episodes
          for (const episodeDoc of episodesSnapshot.docs) {
            const episodeData = episodeDoc.data();
            const episodeId = episodeDoc.id;
            const episodeSeason = episodeData.season || "1";
            
            // Calculate ratings
            const ratings = {};
            let totalScore = 0;
            let maximumPossibleScore = 0;
            
            if (episodeData.ratings) {
              Object.entries(episodeData.ratings).forEach(([raterId, ratingData]) => {
                let raterName = '';
                if (ratingData.raterName) {
                  raterName = ratingData.raterName;
                } else if (ratingData.name) {
                  raterName = ratingData.name;
                }
                
                ratings[raterId] = {
                  rating: ratingData.score || 0,
                  name: raterName,
                  timestamp: ratingData.timestamp || new Date(),
                  comment: ratingData.comment || ''
                };
                
                totalScore += ratingData.score || 0;
                maximumPossibleScore += 10;
              });
            }
            
            // Update season scores
            if (!seasonScores[episodeSeason]) {
              seasonScores[episodeSeason] = {
                seasonEarnedScore: 0,
                seasonPossibleScore: 0
              };
            }
            
            seasonScores[episodeSeason].seasonEarnedScore += totalScore;
            seasonScores[episodeSeason].seasonPossibleScore += maximumPossibleScore;
            
            showPossibleScore += maximumPossibleScore;
            showEarnedScore += totalScore;
            
            // Create episode document
            const newEpisodeData = {
              id: episodeId,
              episodeTitle: episodeData.title || '',
              episodeDescription: episodeData.description || '',
              episodeImage: episodeData.imageUrl || '',
              episodeDuration: parseInt(episodeData.duration) || 22,
              episodeCreationDate: episodeData.createdAt || new Date(),
              episodeDate: episodeData.date || new Date(),
              episodeShowId: showId,
              episodeSeason: parseInt(episodeData.season) || 1,
              episodeCreator: ownerUserId,
              episodeScenes: episodeData.supplementalImages || [],
              episodeRatings: ratings,
              collaborators: {},
              episodeTotalScore: totalScore,
              episodeMaximumPossibleScore: maximumPossibleScore
            };
            
            // Set episode in show's subcollection
            batch.set(doc(db, `shows/${showId}/episodes`, episodeId), newEpisodeData);
          }
          
          // Update show scores
          showData.showPossibleScore = showPossibleScore;
          showData.showEarnedScore = showEarnedScore;
          showData.seasonScores = seasonScores;
        }
        
        // Set show document
        batch.set(doc(db, 'shows', showId), showData);
        
        // Add show reference to owner's shows subcollection
        batch.set(doc(db, `users/${ownerUserId}/shows`, showId), {
          id: showId,
          showName: showData.showName,
          addedAt: new Date()
        });
        
        // Commit changes
        await batch.commit();
        
        processedCount++;
        setTranslationProgress(Math.floor((processedCount / totalToProcess) * 100));
      }
      
      setTranslationComplete(true);
    } catch (error) {
      console.error("Error migrating special users:", error);
      alert(`Migration error: ${error.message}`);
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#131313',
      color: 'white',
      padding: '1rem'
    }}>
      {/* Header */}
      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem'
      }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600' }}>Spiel</h1>
        <div>
          <button
            onClick={() => navigate('/legal')}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#8E8E8E',
              marginRight: '1rem',
              cursor: 'pointer'
            }}
          >
            Legal
          </button>
          <button
            onClick={() => navigate('/login')}
            style={{
              backgroundColor: '#6600CC',
              border: 'none',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              cursor: 'pointer'
            }}
          >
            Get Started
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '4rem auto',
        padding: '0 1rem',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        gap: '4rem'
      }}>
        {/* Right side screenshot for mobile */}
        {isMobile && (
          <div style={{
            position: 'relative',
            width: '200px',
            marginBottom: '-2rem',
            alignSelf: 'center'
          }}>
            <img
              src={ScreenShotMain}
              alt="Speil App Interface"
              style={{
                width: '200px',
                height: 'auto',
              }}
            />
            {/* Fade overlays */}
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '40px',
              height: '100%',
              background: 'linear-gradient(to right, transparent, #131313)',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '40px',
              background: 'linear-gradient(to bottom, transparent, #131313)',
              pointerEvents: 'none'
            }} />
          </div>
        )}

        {/* Left side content */}
        <div style={{
          flex: '1',
          textAlign: 'left'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '2rem',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <img 
              src={SpielLogo} 
              alt="Spiel Logo" 
              style={{
                width: '4rem',
                height: '4rem'
              }}
            />
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold'
            }}>
              Spiel
            </h1>
          </div>

          <p style={{
            fontSize: '1.1rem',
            color: '#8E8E8E',
            marginBottom: '2rem',
            lineHeight: '1.6',
            textAlign: 'left'
          }}>
            Speil is a digital diary where you and your friends can organize life into shows, seasons, and episodes. You can then build your catalog, rate your friends episodes, and compete for the top streak and score. 
          </p>

          <div style={{
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <button
              onClick={() => navigate('/login')}
              style={{
                backgroundColor: '#6600CC',
                border: 'none',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '0.5rem',
                fontSize: '1.1rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Start Your Show
            </button>
          </div>
        </div>

        {/* Right side screenshot for desktop */}
        {!isMobile && (
          <div style={{
            flex: '1',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <div style={{
              position: 'relative',
              width: '300px'
            }}>
              <img
                src={ScreenShotMain}
                alt="Speil App Interface"
                style={{
                  width: '300px',
                  height: 'auto',
                }}
              />
              {/* Right fade overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '40px',
                height: '100%',
                background: 'linear-gradient(to right, transparent, #131313)',
                pointerEvents: 'none'
              }} />
              {/* Bottom fade overlay */}
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '40px',
                background: 'linear-gradient(to bottom, transparent, #131313)',
                pointerEvents: 'none'
              }} />
            </div>
          </div>
        )}
      </div>

      {/* Features Section with Screenshots */}
      <div style={{
        maxWidth: '1200px',
        margin: '6rem auto',
        marginTop: isMobile ? '100px' : '200px',
        padding: '0 1rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '3rem',
          alignItems: 'center'
        }}>
          <div style={{}}>
            <div style={{background: 'rgb(200,200,200,.5)', backdropFilter: 'blur(10px)', padding: '2px 20px', borderRadius: '20px', marginBottom: '-400px', zIndex: '20'}}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Build Your Shows</h3>
            <p style={{ color: 'lightgrey', marginBottom: '2rem' }}>
              Design your personal TV show with seasons, episodes, and a cast of characters from your life.
            </p>
            </div>
            <img
              src={ScreenShotShow}
              alt="Show Creation"
              style={{
                width: '100%',
                borderRadius: '0.5rem',
                zIndex: '10',
                boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
              }}
            />
          </div>
          
          <div style={{
            marginTop: isMobile ? '190px' : '80px'
          }}>
          <div style={{background: 'rgb(200,200,200,.5)', backdropFilter: 'blur(10px)', padding: '2px 20px', borderRadius: '20px', marginBottom: '-400px', zIndex: '20'}}>
            
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Adventures As Episodes</h3>
            <p style={{ color: 'lightgrey', marginBottom: '2rem' }}>
              Document your daily adventures with photos and descriptions, organized like TV episodes.
            </p>
            </div>
            <img
              src={ScreenShotEpisodes}
              alt="Episode Creation"
              style={{
                width: '100%',
                borderRadius: '0.5rem',
                boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
              }}
            />
          </div>
          
          <div style={{
            marginTop: isMobile ? '320px' : '270px'
          }}>
          <div style={{background: 'rgb(200,200,200,.5)', backdropFilter: 'blur(10px)', padding: '2px 20px', borderRadius: '20px', marginBottom: '-550px', zIndex: '20'}}>
            
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Rate and Review</h3>
            <p style={{ color: 'lightgrey', marginBottom: '2rem' }}>
              Let friends rate and comment on your episodes, creating an interactive experience.
            </p>
            </div>
            <img
              src={ScreenShotRate}
              alt="Rating System"
              style={{
                width: '100%',
                borderRadius: '0.5rem',
                boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
              }}
            />
          </div>
        </div>
      </div>

      {/* Admin Data Translation Section (hidden for normal users) */}
      <div style={{
        maxWidth: '600px',
        margin: '4rem auto',
        padding: '1rem',
        backgroundColor: '#1f1f1f',
        borderRadius: '0.5rem',
        display: window.location.search.includes('admin=true') ? 'block' : 'none'
      }}>
        <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Data Structure Translation</h3>
        <p style={{ color: '#8E8E8E', marginBottom: '1rem' }}>
          This tool will migrate the existing data structure to the new format.
        </p>
        
        {translationComplete ? (
          <div style={{ color: '#10B981', marginBottom: '1rem' }}>
            Translation complete! All data has been migrated to the new structure.
          </div>
        ) : (
          <>
            {isTranslating && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ 
                  width: '100%', 
                  height: '8px', 
                  backgroundColor: '#262626',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${translationProgress}%`,
                    height: '100%',
                    backgroundColor: '#6600CC',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ 
                  color: '#8E8E8E', 
                  fontSize: '0.8rem',
                  marginTop: '0.5rem',
                  textAlign: 'center'
                }}>
                  {translationProgress}% complete
                </div>
              </div>
            )}
            
            <button
              onClick={translateDataStructure}
              disabled={isTranslating}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: isTranslating ? '#4B5563' : '#6600CC',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: isTranslating ? 'not-allowed' : 'pointer',
                opacity: isTranslating ? 0.7 : 1
              }}
            >
              {isTranslating ? 'Translating...' : 'Start Translation'}
            </button>
            
            {/* Add the special users migration button */}
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#E5E5E5' }}>Special Users Migration</h4>
              <p style={{ color: '#8E8E8E', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Migrate special show users to the new structure.
              </p>
              <button
                onClick={migrateSpecialUsers}
                disabled={isTranslating}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: isTranslating ? '#4B5563' : '#10B981', // Different color
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: isTranslating ? 'not-allowed' : 'pointer',
                  opacity: isTranslating ? 0.7 : 1
                }}
              >
                {isTranslating ? 'Migrating...' : 'Migrate Special Users'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Footer with Privacy Policy */}
      <footer style={{
        borderTop: '1px solid #262626',
        padding: '2rem 1rem',
        textAlign: 'center',
        marginTop: '4rem'
      }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          color: '#8E8E8E',
          fontSize: '0.9rem'
        }}>
          <a
            href="/legal"
            style={{
              color: '#8E8E8E',
              textDecoration: 'none',
              ':hover': {
                color: '#6600CC'
              }
            }}
          >
            Privacy Policy & Terms of Service
          </a>
          <p>© 2025 4FRNT Education LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 